import * as THREE from 'three';
import { ShipView } from '../game/view.js';
import { glowSprite, streakSprite } from './proctex.js';

/**
 * Pooled particle + trail effects: engine trails, boost flames, wall sparks,
 * drift glow, speed streaks. One shared instanced-ish system, zero allocation
 * per frame after warmup.
 */

const MAX_PARTICLES = 900;

interface Particle {
  alive: boolean;
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
  size: number;
  color: THREE.Color;
  drag: number;
  gravity: number;
}

export class ParticleSystem {
  points: THREE.Points;
  private particles: Particle[] = [];
  private geometry: THREE.BufferGeometry;
  private positions: Float32Array;
  private colors: Float32Array;
  private sizes: Float32Array;
  private cursor = 0;
  private material: THREE.PointsMaterial;

  constructor(scene: THREE.Scene, private intensity: number) {
    this.geometry = new THREE.BufferGeometry();
    this.positions = new Float32Array(MAX_PARTICLES * 3);
    this.colors = new Float32Array(MAX_PARTICLES * 3);
    this.sizes = new Float32Array(MAX_PARTICLES);
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));
    this.material = new THREE.PointsMaterial({
      size: 0.5,
      map: glowSprite(),
      transparent: true,
      opacity: 0.9,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.particles.push({
        alive: false,
        pos: new THREE.Vector3(),
        vel: new THREE.Vector3(),
        life: 0,
        maxLife: 1,
        size: 1,
        color: new THREE.Color(),
        drag: 0,
        gravity: 0,
      });
    }
    scene.add(this.points);
  }

  emit(
    x: number,
    y: number,
    z: number,
    vx: number,
    vy: number,
    vz: number,
    color: THREE.Color | number,
    life: number,
    size: number,
    drag = 2,
    gravity = 0
  ): void {
    if (Math.random() > this.intensity) return;
    const p = this.particles[this.cursor];
    this.cursor = (this.cursor + 1) % MAX_PARTICLES;
    p.alive = true;
    p.pos.set(x, y, z);
    p.vel.set(vx, vy, vz);
    p.life = life;
    p.maxLife = life;
    p.size = size;
    if (typeof color === 'number') p.color.setHex(color);
    else p.color.copy(color);
    p.drag = drag;
    p.gravity = gravity;
  }

  update(dt: number): void {
    let count = 0;
    for (const p of this.particles) {
      if (!p.alive) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.alive = false;
        continue;
      }
      p.vel.multiplyScalar(Math.max(0, 1 - p.drag * dt));
      p.vel.y -= p.gravity * dt;
      p.pos.addScaledVector(p.vel, dt);
      const f = p.life / p.maxLife;
      this.positions[count * 3] = p.pos.x;
      this.positions[count * 3 + 1] = p.pos.y;
      this.positions[count * 3 + 2] = p.pos.z;
      this.colors[count * 3] = p.color.r * f;
      this.colors[count * 3 + 1] = p.color.g * f;
      this.colors[count * 3 + 2] = p.color.b * f;
      count++;
    }
    this.geometry.setDrawRange(0, count);
    (this.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.geometry.attributes.color as THREE.BufferAttribute).needsUpdate = true;
  }

  dispose(): void {
    this.points.removeFromParent();
    this.geometry.dispose();
    this.material.map?.dispose();
    this.material.dispose();
  }
}

/** Ribbon trail behind each engine. */
export class EngineTrail {
  mesh: THREE.Mesh;
  private geometry: THREE.BufferGeometry;
  private positions: Float32Array;
  private history: THREE.Vector3[] = [];
  private historyRight: THREE.Vector3[] = [];
  private readonly segments = 16;
  private material: THREE.MeshBasicMaterial;
  private headWidth = 0.22;

  constructor(scene: THREE.Scene, color: THREE.ColorRepresentation) {
    this.geometry = new THREE.BufferGeometry();
    this.positions = new Float32Array((this.segments + 1) * 2 * 3);
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    const indices: number[] = [];
    for (let i = 0; i < this.segments; i++) {
      const a = i * 2;
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    this.geometry.setIndex(indices);
    // alpha fade along the trail via vertex colors
    const alphas = new Float32Array((this.segments + 1) * 2 * 3);
    this.geometry.setAttribute('color', new THREE.BufferAttribute(alphas, 3));
    this.material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      vertexColors: true,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    for (let i = 0; i <= this.segments; i++) {
      this.history.push(new THREE.Vector3());
      this.historyRight.push(new THREE.Vector3(1, 0, 0));
    }
    scene.add(this.mesh);
  }

  setColor(c: THREE.ColorRepresentation): void {
    this.material.color.set(c);
  }

  update(head: THREE.Vector3, right: THREE.Vector3, intensity: number, dt: number): void {
    // shift history toward the tail at a rate tied to dt (framerate independent-ish)
    const shift = Math.min(1, dt * 34);
    for (let i = this.segments; i > 0; i--) {
      this.history[i].lerp(this.history[i - 1], shift);
      this.historyRight[i].lerp(this.historyRight[i - 1], shift);
    }
    this.history[0].copy(head);
    this.historyRight[0].copy(right);

    const colAttr = this.geometry.attributes.color as THREE.BufferAttribute;
    for (let i = 0; i <= this.segments; i++) {
      const t = i / this.segments;
      const w = this.headWidth * (1 - t * 0.85) * (0.35 + intensity);
      const p = this.history[i];
      const r = this.historyRight[i];
      this.positions[i * 6] = p.x - r.x * w;
      this.positions[i * 6 + 1] = p.y - r.y * w;
      this.positions[i * 6 + 2] = p.z - r.z * w;
      this.positions[i * 6 + 3] = p.x + r.x * w;
      this.positions[i * 6 + 4] = p.y + r.y * w;
      this.positions[i * 6 + 5] = p.z + r.z * w;
      const a = (1 - t) * Math.min(1, intensity * 1.6);
      colAttr.setXYZ(i * 2, a, a, a);
      colAttr.setXYZ(i * 2 + 1, a, a, a);
    }
    (this.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    colAttr.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}

/** Speed streaks rushing past the camera at high velocity. */
export class SpeedStreaks {
  private mesh: THREE.InstancedMesh;
  private offsets: THREE.Vector3[] = [];
  private material: THREE.MeshBasicMaterial;
  private dummy = new THREE.Object3D();
  private readonly count = 70;

  constructor(scene: THREE.Scene) {
    const geo = new THREE.PlaneGeometry(0.05, 3.2);
    this.material = new THREE.MeshBasicMaterial({
      map: streakSprite(),
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.InstancedMesh(geo, this.material, this.count);
    this.mesh.frustumCulled = false;
    for (let i = 0; i < this.count; i++) {
      this.offsets.push(
        new THREE.Vector3((Math.random() - 0.5) * 26, (Math.random() - 0.5) * 16, Math.random() * 60 - 30)
      );
    }
    scene.add(this.mesh);
  }

  update(camPos: THREE.Vector3, camQuat: THREE.Quaternion, fwd: THREE.Vector3, speedNorm: number, boost: boolean): void {
    const vis = Math.max(0, speedNorm - 0.55) * 1.6 + (boost ? 0.35 : 0);
    this.material.opacity = Math.min(0.5, vis * 0.4);
    if (this.material.opacity <= 0.01) {
      this.mesh.visible = false;
      return;
    }
    this.mesh.visible = true;
    for (let i = 0; i < this.count; i++) {
      const o = this.offsets[i];
      // wrap streaks in a tube around/ahead of the camera
      o.z -= speedNorm * 3.2;
      if (o.z < -30) o.z += 60;
      this.dummy.position.copy(o).applyQuaternion(camQuat).add(camPos);
      this.dummy.quaternion.copy(camQuat);
      this.dummy.rotateX(Math.PI / 2 - 0.12);
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(i, this.dummy.matrix);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.material.map?.dispose();
    this.material.dispose();
  }
}

/** Per-ship FX bundle: trails + local particle emission based on ship state. */
export class ShipEffects {
  trails: EngineTrail[] = [];
  private tmpV = new THREE.Vector3();
  private tmpR = new THREE.Vector3();
  private tmpQ = new THREE.Quaternion();

  constructor(
    scene: THREE.Scene,
    private engines: THREE.Vector3[],
    glowColor: THREE.ColorRepresentation
  ) {
    for (let i = 0; i < engines.length; i++) {
      this.trails.push(new EngineTrail(scene, glowColor));
    }
  }

  update(ship: ShipView, particles: ParticleSystem, dt: number): void {
    this.tmpQ.set(ship.quat.x, ship.quat.y, ship.quat.z, ship.quat.w);
    this.tmpR.set(1, 0, 0).applyQuaternion(this.tmpQ);
    const speedNorm = Math.min(1, ship.speed / 100);
    const intensity = ship.visible ? (ship.boosting ? 1.2 : speedNorm * 0.75) : 0;

    for (let i = 0; i < this.trails.length; i++) {
      const e = this.engines[i];
      this.tmpV.set(e.x, e.y, e.z).applyQuaternion(this.tmpQ);
      this.tmpV.x += ship.pos.x;
      this.tmpV.y += ship.pos.y;
      this.tmpV.z += ship.pos.z;
      this.trails[i].update(this.tmpV, this.tmpR, intensity, dt);

      // boost flame particles
      if (ship.boosting && ship.visible) {
        particles.emit(
          this.tmpV.x,
          this.tmpV.y,
          this.tmpV.z,
          -ship.vel.x * 0.25 + (Math.random() - 0.5) * 3,
          -ship.vel.y * 0.25 + (Math.random() - 0.5) * 3,
          -ship.vel.z * 0.25 + (Math.random() - 0.5) * 3,
          Math.random() < 0.5 ? 0xff8a2a : 0x31c8ff,
          0.35,
          0.85,
          3
        );
      }
    }

    // wall grind sparks
    if (ship.wallContact > 0.08 && ship.visible && ship.grounded) {
      for (let k = 0; k < 2; k++) {
        particles.emit(
          ship.pos.x + (Math.random() - 0.5) * 1.6,
          ship.pos.y + (Math.random() - 0.3) * 0.8,
          ship.pos.z + (Math.random() - 0.5) * 1.6,
          -ship.vel.x * 0.1 + (Math.random() - 0.5) * 14,
          Math.random() * 7,
          -ship.vel.z * 0.1 + (Math.random() - 0.5) * 14,
          Math.random() < 0.7 ? 0xffc24d : 0xffffff,
          0.4 + Math.random() * 0.3,
          0.4,
          1.5,
          22
        );
      }
    }

    // drift shimmer
    if (ship.drifting && ship.grounded && ship.speed > 30 && ship.visible) {
      particles.emit(
        ship.pos.x - this.tmpR.x * (Math.random() < 0.5 ? 1.4 : -1.4),
        ship.pos.y - 0.6,
        ship.pos.z - this.tmpR.z * (Math.random() < 0.5 ? 1.4 : -1.4),
        (Math.random() - 0.5) * 4,
        1 + Math.random() * 2,
        (Math.random() - 0.5) * 4,
        0x9a5cff,
        0.3,
        0.5,
        4
      );
    }
  }

  dispose(): void {
    for (const t of this.trails) t.dispose();
  }
}
